import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import {
  canChangeAvatar,
  getUserAvatarUrl,
  getUserDisplayName,
  getUserInitials,
  getUserSession,
  mergeSessionUser,
  saveUserSession,
} from '../services/authService';
import {
  fetchUserProfile,
  formatProfileDate,
  getChildrenLabel,
  getGenderLabel,
  getProfileSubtitle,
  removeUserAvatar,
  updateUserProfile,
  uploadUserAvatar,
} from '../services/profileService';
import { getPersonNameError, sanitizePersonNameInput } from '../utils/personName';
import '../styles/profile.css';

const UserFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
  </svg>
);

const CalendarFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M4 10h16" />
  </svg>
);

const PhoneFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 4h4l2 3h4a2 2 0 012 2v11H4V6a2 2 0 012-2z" />
  </svg>
);

const MailFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const LocationFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 21s6-5.2 6-10a6 6 0 10-12 0c0 4.8 6 10 6 10z" />
    <circle cx="12" cy="11" r="2.5" />
  </svg>
);

const FootprintFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 18c1.5-2 2-4 2-6a3 3 0 016 0c0 2 .5 4 2 6" />
    <path d="M6 14c1-1.5 1.5-3 1.5-4.5a2.5 2.5 0 015 0C12.5 11 13 12.5 14 14" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20h4l10-10-4-4L4 16v4z" />
    <path d="M13 7l4 4" />
  </svg>
);

const IdCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="11" r="2" />
    <path d="M5.5 16c0-1.5 1.4-2.5 3-2.5s3 1 3 2.5M14 9h5M14 12h5M14 15h3" />
  </svg>
);

const ProfileField = ({
  icon,
  label,
  value,
  editable = false,
  onEdit,
  variant = 'default',
  missing = false,
}) => (
  <div className={`profile-field profile-field--${variant}${missing ? ' profile-field--missing' : ''}`}>
    {label ? <span className="profile-field-label">{label}</span> : null}
    <div className="profile-field-body">
      {variant !== 'gender' ? <span className="profile-field-icon">{icon}</span> : null}
      <span className="profile-field-value">{value || (missing ? 'Pendiente' : '—')}</span>
      {editable ? (
        <button type="button" className="profile-field-edit" onClick={onEdit} aria-label={`Editar ${label}`}>
          <EditIcon />
        </button>
      ) : null}
    </div>
  </div>
);

const buildFallbackProfile = (sessionUser) => ({
  id: sessionUser.id,
  email: sessionUser.email || '',
  nombre: sessionUser.nombre || '',
  apellido: sessionUser.apellido || '',
  telefono: sessionUser.telefono || '',
  ubicacion: sessionUser.ubicacion || '',
  documento_tipo: sessionUser.documento_tipo || null,
  documento_numero: sessionUser.documento_numero || '',
  tipo: sessionUser.tipo || 'paciente',
  avatar_url: sessionUser.avatar_url || null,
  fecha_nacimiento: sessionUser.fecha_nacimiento || null,
  genero: sessionUser.genero || null,
  sin_hijos: Boolean(sessionUser.sin_hijos),
  total_hijos: sessionUser.total_hijos || 0,
  roles: sessionUser.roles || [],
  perfil_completo: sessionUser.perfil_completo !== false,
  campos_faltantes: sessionUser.campos_faltantes || [],
});

const editFieldLabels = {
  nombre: 'nombre completo',
  telefono: 'teléfono',
  ubicacion: 'ubicación',
  documento_numero: 'documento de identidad',
  fecha_nacimiento: 'fecha de nacimiento',
  genero: 'género',
};

const pendingFieldLabels = {
  nombre: 'Nombre completo',
  telefono: 'Teléfono',
  ubicacion: 'Ubicación',
  documento: 'Documento',
  fecha_nacimiento: 'Fecha de nacimiento',
  genero: 'Género',
  hijos: 'Pacientes',
  pacientes: 'Pacientes',
  acepta_terminos: 'Términos y condiciones',
};

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ProfilePage = () => {
  const history = useHistory();
  const sessionUser = getUserSession();
  const sessionUserId = sessionUser ? sessionUser.id : null;
  const avatarInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDocType, setEditDocType] = useState('cedula');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isAvatarSaving, setIsAvatarSaving] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState('');

  useEffect(() => {
    if (!sessionUserId) {
      setIsLoading(false);
      return;
    }

    fetchUserProfile(sessionUserId)
      .then((data) => {
        setProfile(data);
        setLoadWarning('');
        mergeSessionUser({
          perfil_completo: data.perfil_completo,
          campos_faltantes: data.campos_faltantes || [],
        });
      })
      .catch((error) => {
        const currentSession = getUserSession() || sessionUser;
        setProfile(buildFallbackProfile(currentSession));
        setLoadWarning(
          error.message === 'Network Error'
            ? 'No se pudo conectar con el servidor. Se muestran los datos básicos de tu sesión.'
            : error.message || 'No se pudo cargar el perfil completo.'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [sessionUserId]);

  if (!sessionUser) {
    return <Redirect to="/iniciar-sesion" />;
  }

  const displayProfile = profile || buildFallbackProfile(sessionUser);
  const displayName = getUserDisplayName(displayProfile);
  const subtitle = getProfileSubtitle(displayProfile);
  const avatarUrl = getUserAvatarUrl(displayProfile);
  const avatarInitials = getUserInitials(displayProfile);
  const userCanChangeAvatar = canChangeAvatar(displayProfile);

  const handleAvatarButtonClick = () => {
    if (!userCanChangeAvatar || isAvatarSaving || !avatarInputRef.current) {
      return;
    }

    avatarInputRef.current.click();
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file || !sessionUserId) {
      return;
    }

    event.target.value = '';

    if (AVATAR_ACCEPTED_TYPES.indexOf(file.type) === -1) {
      setAvatarMessage('Usa una imagen JPG, PNG, WEBP o GIF.');
      return;
    }

    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarMessage('La imagen no puede superar 2 MB.');
      return;
    }

    setIsAvatarSaving(true);
    setAvatarMessage('');

    const reader = new FileReader();

    reader.onload = () => {
      uploadUserAvatar(sessionUserId, reader.result)
        .then((data) => {
          const nextAvatarUrl = data.avatar_url || null;
          setProfile((current) => {
            const base = current || buildFallbackProfile(sessionUser);
            return { ...base, avatar_url: nextAvatarUrl };
          });
          mergeSessionUser({ avatar_url: nextAvatarUrl });
          setAvatarMessage('Avatar actualizado correctamente.');
        })
        .catch((error) => {
          setAvatarMessage(error.message || 'No se pudo actualizar el avatar.');
        })
        .finally(() => {
          setIsAvatarSaving(false);
        });
    };

    reader.onerror = () => {
      setAvatarMessage('No se pudo leer la imagen seleccionada.');
      setIsAvatarSaving(false);
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    if (!sessionUserId || !avatarUrl || isAvatarSaving) {
      return;
    }

    setIsAvatarSaving(true);
    setAvatarMessage('');

    removeUserAvatar(sessionUserId)
      .then(() => {
        setProfile((current) => {
          const base = current || buildFallbackProfile(sessionUser);
          return { ...base, avatar_url: null };
        });
        mergeSessionUser({ avatar_url: null });
        setAvatarMessage('Avatar eliminado.');
      })
      .catch((error) => {
        setAvatarMessage(error.message || 'No se pudo eliminar el avatar.');
      })
      .finally(() => {
        setIsAvatarSaving(false);
      });
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || '');
    setSaveMessage('');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const refreshProfileState = (freshProfile, savedFields = {}) => {
    const mergedProfile = { ...freshProfile, ...savedFields };
    setProfile(mergedProfile);
    const updatedSession = mergeSessionUser({
      nombre: mergedProfile.nombre,
      apellido: mergedProfile.apellido,
      telefono: mergedProfile.telefono,
      ubicacion: mergedProfile.ubicacion,
      fecha_nacimiento: mergedProfile.fecha_nacimiento,
      genero: mergedProfile.genero,
      avatar_url: mergedProfile.avatar_url,
      perfil_completo: mergedProfile.perfil_completo,
      campos_faltantes: mergedProfile.campos_faltantes || [],
    });
    if (updatedSession) {
      saveUserSession(updatedSession);
    }
  };

  const handleSaveField = () => {
    if (!editingField || !sessionUserId) {
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    const trimmedValue = editValue.trim();
    let payload = {};

    if (editingField === 'genero' && !trimmedValue) {
      setSaveMessage('Selecciona un género válido.');
      setIsSaving(false);
      return;
    }

    if (editingField === 'nombre') {
      const nameError = getPersonNameError(trimmedValue);
      if (nameError) {
        setSaveMessage(nameError);
        setIsSaving(false);
        return;
      }
    }

    if (editingField === 'documento_numero') {
      payload = { documento_numero: trimmedValue.toUpperCase(), documento_tipo: editDocType };
    } else if (editingField === 'genero') {
      payload = { genero: trimmedValue };
    } else if (editingField === 'nombre') {
      payload = { nombre: trimmedValue, apellido: '' };
    } else {
      payload = { [editingField]: trimmedValue };
    }

    updateUserProfile(sessionUserId, payload)
      .then(() => fetchUserProfile(sessionUserId))
      .then((freshProfile) => {
        const savedFields =
          editingField === 'documento_numero'
            ? {
                documento_numero: payload.documento_numero,
                documento_tipo: payload.documento_tipo,
              }
            : editingField === 'nombre'
              ? { nombre: payload.nombre, apellido: payload.apellido || '' }
              : payload;

        refreshProfileState(freshProfile, savedFields);
        setEditingField(null);
        setEditValue('');
        setSaveMessage(
          freshProfile.perfil_completo
            ? 'Perfil completado. Ya puedes usar Beyond con normalidad.'
            : 'Cambios guardados correctamente.'
        );
        setLoadWarning('');
      })
      .catch((error) => {
        setSaveMessage(error.message || 'No se pudo guardar el cambio.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const isEspecialista = displayProfile.tipo === 'especialista';
  const isRepresentante = displayProfile.tipo === 'paciente';
  const missingFields = displayProfile.campos_faltantes || [];
  const isMissing = (field) => missingFields.includes(field);
  const pendingSummary = missingFields
    .map((field) => pendingFieldLabels[field] || field)
    .join(', ');
  const profileIncomplete = displayProfile.perfil_completo === false;

  return (
    <div className="profile-root">
      <div className="profile-layout">
        <ProfileSidebar activeId="perfil" />

        <section className="profile-main">
          {isLoading ? <p className="profile-status">Cargando perfil...</p> : null}

          {!isLoading ? (
            <>
              {loadWarning ? (
                <p className="profile-status profile-status--warning">{loadWarning}</p>
              ) : null}

              {profileIncomplete && pendingSummary ? (
                <div className="profile-pending-banner" role="status">
                  <strong>Completa tu perfil.</strong> Pendiente: {pendingSummary}. Usa el ícono de
                  editar en cada campo resaltado.
                </div>
              ) : null}

              <div className="profile-header">
                <div className="profile-avatar-wrap">
                  <span
                    className={`profile-avatar${isAvatarSaving ? ' profile-avatar--loading' : ''}`}
                    style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
                  >
                    {!avatarUrl ? avatarInitials : null}
                  </span>
                  {userCanChangeAvatar ? (
                    <>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="profile-avatar-input"
                        onChange={handleAvatarFileChange}
                      />
                      <button
                        type="button"
                        className="profile-avatar-edit"
                        aria-label="Cambiar foto"
                        onClick={handleAvatarButtonClick}
                        disabled={isAvatarSaving}
                      >
                        <EditIcon />
                      </button>
                    </>
                  ) : null}
                </div>

                <div className="profile-header-text">
                  <h1 className="profile-name">{displayName}</h1>
                  <p className="profile-subtitle">{subtitle}</p>
                  {userCanChangeAvatar ? (
                    <div className="profile-avatar-actions">
                      <button
                        type="button"
                        className="profile-avatar-action"
                        onClick={handleAvatarButtonClick}
                        disabled={isAvatarSaving}
                      >
                        {isAvatarSaving ? 'Guardando foto…' : 'Cambiar foto'}
                      </button>
                      {avatarUrl ? (
                        <button
                          type="button"
                          className="profile-avatar-action profile-avatar-action--muted"
                          onClick={handleRemoveAvatar}
                          disabled={isAvatarSaving}
                        >
                          Quitar foto
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {avatarMessage ? (
                <p
                  className={`profile-status${
                    avatarMessage.includes('correctamente') || avatarMessage.includes('eliminado')
                      ? ''
                      : ' profile-status--error'
                  }`}
                >
                  {avatarMessage}
                </p>
              ) : null}

              {saveMessage ? (
                <p
                  className={`profile-status${
                    saveMessage.includes('guardados') || saveMessage.includes('completado')
                      ? ''
                      : ' profile-status--error'
                  }`}
                >
                  {saveMessage}
                </p>
              ) : null}

              {editingField ? (
                <div className="profile-edit-panel">
                  <label className="profile-edit-label" htmlFor="profile-edit-input">
                    Editar {editFieldLabels[editingField] || editingField}
                  </label>
                  <div className="profile-edit-row">
                    {editingField === 'documento_numero' ? (
                      <select
                        className="profile-edit-select"
                        value={editDocType}
                        onChange={(event) => setEditDocType(event.target.value)}
                      >
                        <option value="cedula">Cédula</option>
                        <option value="pasaporte">Pasaporte</option>
                        <option value="otro">Otro</option>
                      </select>
                    ) : null}
                    {editingField === 'genero' ? (
                      <select
                        id="profile-edit-input"
                        className="profile-edit-select"
                        value={editValue}
                        onChange={(event) => setEditValue(event.target.value)}
                      >
                        <option value="">Seleccione</option>
                        <option value="mujer">Femenino</option>
                        <option value="hombre">Masculino</option>
                      </select>
                    ) : (
                      <input
                        id="profile-edit-input"
                        className="profile-edit-input"
                        type={editingField === 'fecha_nacimiento' ? 'date' : 'text'}
                        value={editValue}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEditValue(
                            editingField === 'nombre' ? sanitizePersonNameInput(value) : value
                          );
                        }}
                        placeholder={
                          editingField === 'ubicacion' ? 'Ciudad, país o zona online' : undefined
                        }
                      />
                    )}
                    <button
                      type="button"
                      className="profile-edit-save"
                      onClick={handleSaveField}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button type="button" className="profile-edit-cancel" onClick={cancelEditing}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="profile-grid">
                <ProfileField
                  icon={<UserFieldIcon />}
                  label="Nombre completo"
                  value={displayName}
                  variant="muted"
                  missing={isMissing('nombre')}
                  editable
                  onEdit={() => startEditing('nombre', displayName)}
                />
                <ProfileField
                  icon={<CalendarFieldIcon />}
                  label="Fecha de nacimiento"
                  value={formatProfileDate(displayProfile.fecha_nacimiento)}
                  variant="muted"
                  missing={isMissing('fecha_nacimiento')}
                  editable
                  onEdit={() => startEditing('fecha_nacimiento', displayProfile.fecha_nacimiento || '')}
                />
                <ProfileField
                  label="Género"
                  value={getGenderLabel(displayProfile.genero)}
                  variant={displayProfile.genero ? 'gender' : 'muted'}
                  missing={isMissing('genero')}
                  editable
                  onEdit={() => startEditing('genero', displayProfile.genero || '')}
                />
                <ProfileField
                  icon={<PhoneFieldIcon />}
                  label="Teléfono"
                  value={displayProfile.telefono}
                  editable={isMissing('telefono') || Boolean(displayProfile.telefono)}
                  variant="outline"
                  missing={isMissing('telefono')}
                  onEdit={() => startEditing('telefono', displayProfile.telefono)}
                />
                <ProfileField
                  icon={<MailFieldIcon />}
                  label="Correo electrónico"
                  value={displayProfile.email}
                  variant="outline"
                />
                {!isRepresentante ? (
                  <ProfileField
                    icon={<LocationFieldIcon />}
                    label="Ubicación"
                    value={displayProfile.ubicacion}
                    editable={isMissing('ubicacion') || Boolean(displayProfile.ubicacion)}
                    variant="outline"
                    missing={isMissing('ubicacion')}
                    onEdit={() => startEditing('ubicacion', displayProfile.ubicacion)}
                  />
                ) : null}
                {isRepresentante ? (
                  <ProfileField
                    icon={<FootprintFieldIcon />}
                    label="Pacientes"
                    value={getChildrenLabel(displayProfile)}
                    variant="outline"
                    missing={isMissing('pacientes') || isMissing('hijos')}
                    editable
                    onEdit={() => history.push('/perfil/pacientes')}
                  />
                ) : null}
                {!isRepresentante ? (
                  <ProfileField
                    icon={<IdCardIcon />}
                    label="Documento"
                    value={displayProfile.documento_numero}
                    editable={!isEspecialista && (isMissing('documento') || Boolean(displayProfile.documento_numero))}
                    variant="outline"
                    missing={isMissing('documento')}
                    onEdit={() => {
                      setEditDocType(displayProfile.documento_tipo || 'cedula');
                      startEditing('documento_numero', displayProfile.documento_numero);
                    }}
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
